import type Location from './Location';
import {bootstrapper, defineProperty} from './shared';
import {rejectImports} from './utils';

const cssStyleSheetMethods: Array<keyof CSSStyleSheet> = [
  'addRule',
  'deleteRule',
  'insertRule',
  'removeRule',
];

const NonConstructedStyleSheet = CSSStyleSheet;
const nonConstructedProto = NonConstructedStyleSheet.prototype;

export function isCSSStyleSheetInstance(instance: object): boolean {
  return typeof instance === 'object'
    ? proto.isPrototypeOf(instance) ||
        nonConstructedProto.isPrototypeOf(instance)
    : false;
}

export function isNonConstructedStyleSheetInstance(instance: object): boolean {
  return typeof instance === 'object'
    ? nonConstructedProto.isPrototypeOf(instance)
    : false;
}

/*
 * Private properties
 */

/**
 * Basic style element is a sample element that contains all the CSS of the
 * current constructable stylesheet. The document or custom elements can
 * adopt constructable stylesheet; in this case, basic stylesheet's CSS is
 * reflected in document/custom element internal <style> elements.
 */
const $basicStyleElement = new WeakMap<
  ConstructedStyleSheet,
  HTMLStyleElement
>();

/**
 * Contains all locations associated with the current ConstructedStyleSheet.
 */
const $locations = new WeakMap<ConstructedStyleSheet, Location[]>();

/**
 * Adopter is a `<style>` element that belongs to the document or a custom
 * element and contains the content of the basic stylesheet.
 *
 * This property contains a map of `<style>` adopter associated with locations.
 */
const $adoptersByLocation = new WeakMap<
  ConstructedStyleSheet,
  WeakMap<Location, HTMLStyleElement>
>();

type CSSStyleSheetMethodMeta = Readonly<{
  args: IArguments;
  method: string;
}>;

/**
 * Contains all the methods called for the constructable style sheet. Used to
 * reflect these methods in created or restyled `<style>` adopters.
 */
const $appliedMethods = new WeakMap<
  ConstructedStyleSheet,
  Array<CSSStyleSheetMethodMeta>
>();

/*
 * Package-level control functions
 */

export function addAdopterLocation(
  sheet: ConstructedStyleSheet,
  location: Location,
): HTMLStyleElement {
  const adopter = document.createElement('style');
  adopter.setAttribute('data-layer', sheet.name ?? '');

  // Note: this value is included to match the sail-next inner text of style nodes. This is intentional and is done so that the
  // required `style-src` CSP directive required for this node is the same as the one required for sail-next.
  adopter.innerText = '/**/';
  $adoptersByLocation.get(sheet)!.set(location, adopter);
  $locations.get(sheet)!.push(location);

  return adopter;
}

export function getAdopterByLocation(
  sheet: ConstructedStyleSheet,
  location: Location,
): HTMLStyleElement | undefined {
  return $adoptersByLocation.get(sheet)!.get(location);
}

export function removeAdopterLocation(
  sheet: ConstructedStyleSheet,
  location: Location,
): void {
  $adoptersByLocation.get(sheet)!.delete(location);
  $locations.set(
    sheet,
    $locations.get(sheet)!.filter((_location) => _location !== location),
  );
}

/**
 * Re-styles a single `<style>` adopter according to the basic style sheet.
 *
 * NOTE: don't use it for disconnected adopters. It will throw an error.
 */
export function restyleAdopter(
  sheet: ConstructedStyleSheet,
  adopter: HTMLStyleElement,
): void {
  adopter.textContent = $basicStyleElement.get(sheet)!.textContent;
  $appliedMethods.get(sheet)!.forEach((command) => {
    try {
      // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'CSSStyleSheet'. No index signature with a parameter of type 'string' was found on type 'CSSStyleSheet'.
      adopter.sheet![command.method].apply(adopter.sheet!, command.args); // eslint-disable-line prefer-spread
    } catch (e) {
      // CSSStyleSheet.insertRule has several syntax restrictions and will
      // throw errors when it encounters certain types of unknown syntax
      // (in particular, unsupported pseudoselectors).
      //
      // We catch these errors for several reasons:
      // * We want to encourage progressive enhancement and leverage new
      //   browser features when possible.
      // * It’s difficult to know which pseudoselectors are supported in
      //   the current browser environment.
      // * Attempting to replace unknown syntax in each rule would penalize
      //   the many valid rules at the expense of the few invalid rules.
      //
      // https://developer.mozilla.org/en-US/docs/Web/API/CSSStyleSheet/insertRule#restrictions
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(e);
      }
    }
  });
}

/*
 * Private methods
 */

/**
 * This method checks if the method of the class is called correctly using the
 * CSSStyleSheet instance. It is necessary to be sure that all private
 * properties are initialized.
 */
function checkInvocationCorrectness(self: ConstructedStyleSheet) {
  if (!$basicStyleElement.has(self)) {
    throw new TypeError('Illegal invocation');
  }
}

/**
 * A replacement for CSSStyleSheet class which is not actually constructable in
 * any browser that does not support Constructable Style Sheet proposal. To
 * fulfil the Constructable Style Sheet specification, it preserves a separate
 * HTMLStyleElement that behaves as a backup for all the `<style>` adopters.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
declare interface ConstructedStyleSheet extends CSSStyleSheet {
  replace(text: string): Promise<ConstructedStyleSheet>;

  replaceSync(text: string): void;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class ConstructedStyleSheet {
  public ownerNode: HTMLStyleElement;

  constructor(public name?: string) {
    this.name = name ?? '';
    const style = document.createElement('style');
    style.setAttribute('data-layer', this.name);

    this.ownerNode = style;

    // Note: this value is included to match the sail-next inner text of style nodes. This is intentional and is done so that the
    // required `style-src` CSP directive required for this node is the same as the one required for sail-next.
    style.innerText = '/**/';
    bootstrapper.body.appendChild(style);

    // Init private properties
    $basicStyleElement.set(this, style);
    $locations.set(this, []);
    $adoptersByLocation.set(this, new WeakMap());
    $appliedMethods.set(this, []);
  }
}

const proto = ConstructedStyleSheet.prototype;

proto.replace = function replace(
  contents: string,
): Promise<ConstructedStyleSheet> {
  try {
    this.replaceSync(contents);

    return Promise.resolve(this);
  } catch (e) {
    return Promise.reject(e);
  }
};

proto.replaceSync = function replaceSync(contents) {
  // CSSStyleSheet.prototype.replaceSync('body {}')
  checkInvocationCorrectness(this);

  if (typeof contents === 'string') {
    const self = this;

    $basicStyleElement.get(self)!.textContent = rejectImports(contents);
    $appliedMethods.set(self, []);

    $locations.get(self)!.forEach((location) => {
      if (location.isConnected()) {
        // Type Note: if location is connected, adopter is already created.
        restyleAdopter(self, getAdopterByLocation(self, location)!);
      }
    });
  }
};

defineProperty(proto, 'cssRules', {
  configurable: true,
  enumerable: true,
  get: function cssRules() {
    // CSSStyleSheet.prototype.cssRules;
    checkInvocationCorrectness(this);

    return $basicStyleElement.get(this)!.sheet!.cssRules;
  },
});

defineProperty(proto, 'media', {
  configurable: true,
  enumerable: true,
  get: function media() {
    // CSSStyleSheet.prototype.media;
    checkInvocationCorrectness(this);

    return $basicStyleElement.get(this)!.sheet!.media;
  },
});

cssStyleSheetMethods.forEach((method) => {
  // @ts-expect-error cannot assign readonly property
  proto[method] = function () {
    const self = this;
    checkInvocationCorrectness(self);

    const args = arguments; // eslint-disable-line prefer-rest-params

    $appliedMethods.get(self)!.push({method, args});

    $locations.get(self)!.forEach((location) => {
      if (location.isConnected()) {
        // Type Note: If location is connected, adopter is already created; and
        // since it is connected to DOM, the sheet cannot be null.
        const sheet = getAdopterByLocation(self, location)!.sheet!;
        // @ts-expect-error object possibly null
        sheet[method].apply(sheet, args); // eslint-disable-line prefer-spread
      }
    });

    const basicSheet = $basicStyleElement.get(self)!.sheet!;
    // @ts-expect-error object possibly null
    return basicSheet[method].apply(basicSheet, args); // eslint-disable-line prefer-spread
  };
});

defineProperty(ConstructedStyleSheet, Symbol.hasInstance, {
  configurable: true,
  value: isCSSStyleSheetInstance,
});

export default ConstructedStyleSheet;
